'use client'

import {
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/utilities/ui'
import { useTranslations } from 'next-intl'
import React from 'react'

import { useTheme } from '@/providers/Theme'

function SunMoonIcon({ isDark }: { isDark: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={20}
      height={20}
      className="shrink-0 text-foreground"
      aria-hidden
    >
      <g
        className={cn(
          'origin-[12px_12px] transition-[opacity,transform] duration-200 ease-out',
          isDark ? 'scale-50 opacity-0' : 'scale-100 opacity-100',
        )}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
      </g>
      <g
        className={cn(
          'origin-[12px_12px] transition-[opacity,transform] duration-200 ease-out',
          isDark ? 'scale-100 opacity-100' : 'scale-50 opacity-0',
        )}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
      </g>
    </svg>
  )
}

function BwSwitchTrack({ on }: { on: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex h-4 w-[26px] shrink-0 items-center rounded-full border border-foreground px-px transition-[background-color] duration-200',
        on ? 'justify-end bg-foreground' : 'justify-start bg-background',
      )}
      aria-hidden
    >
      <span
        className={cn(
          'block size-2.5 shrink-0 rounded-full transition-[background-color] duration-200',
          on ? 'bg-background' : 'bg-foreground',
        )}
      />
    </span>
  )
}

/**
 * 🔴 Duas passadas de propósito (23/08/2026). Desde que o tema saiu do SSR (plano de cache, P0),
 * o HTML do servidor nasce SEMPRE «light»; o ThemeProvider corrige o estado durante o render de
 * hidratação. Se este hook devolvesse o tema real direto, o `aria-checked`/classes do switch
 * divergiriam do HTML servido e o React 19 logaria erro de hidratação para TODO leitor no escuro.
 *
 * Solução: hidratar combinando com o servidor (false) e corrigir num efeito pós-montagem. O
 * «estado errado» dura um frame DENTRO de um menu fechado — invisível. O tema da PÁGINA em si não
 * passa por aqui: é o `themeBootScript` que o aplica antes da pintura.
 */
const assinaturaVazia = () => () => {}

function useIsDark(): boolean {
  const { theme } = useTheme()
  // `useSyncExternalStore` com snapshots fixos: false no servidor/hidratação, true depois —
  // o jeito canônico de «montado?» sem setState em efeito (e sem aviso do react-hooks).
  const mounted = React.useSyncExternalStore(
    assinaturaVazia,
    () => true,
    () => false,
  )
  return mounted && theme === 'dark'
}

/** Botão largo para o sheet mobile (menu hamburger). */
export function ModoEscuroToggleSheet() {
  const t = useTranslations('Theme')
  const { setTheme } = useTheme()
  const isDark = useIsDark()

  const toggle = () => setTheme(isDark ? 'light' : 'dark')

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={t('darkMode')}
      onClick={toggle}
      className={cn(
        'flex w-full items-center justify-between gap-3 rounded-lg border-2 border-foreground bg-background px-3 py-2.5 text-left',
        'transition-[background-color,box-shadow] duration-200',
        'hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      )}
    >
      <span className="flex min-w-0 items-center gap-2.5">
        <SunMoonIcon isDark={isDark} />
        <span className="text-sm font-semibold text-foreground">{t('darkMode')}</span>
      </span>
      <BwSwitchTrack on={isDark} />
    </button>
  )
}

/** Linha do menu rápido (dropdown desktop): alterna ao escolher o item. */
export function ModoEscuroDropdownItem() {
  const t = useTranslations('Theme')
  const { setTheme } = useTheme()
  const isDark = useIsDark()

  return (
    <DropdownMenuItem
      className={cn(
        'flex min-w-[14rem] cursor-pointer items-center justify-between gap-3 rounded-sm py-2.5 pl-2 pr-2',
        'focus:bg-accent',
      )}
      onSelect={(e) => {
        e.preventDefault()
        setTheme(isDark ? 'light' : 'dark')
      }}
    >
      <span className="flex items-center gap-2.5">
        <SunMoonIcon isDark={isDark} />
        <span className="text-sm font-semibold text-foreground">{t('darkMode')}</span>
      </span>
      <BwSwitchTrack on={isDark} />
    </DropdownMenuItem>
  )
}
